/* eslint-disable no-undef */
<template>
  <div class="quick-time-picker">
    <div
      :class="['item', { active: activeIndex === index }]"
      v-for="(item, index) in shortcutArr"
      :key="index"
      @click="handleQuickTimePickerChange(index)"
    >{{ item }}</div>

    <el-date-picker
      v-model="searchTime"
      :type="type"
      align="right"
      start-placeholder="开始日期"
      end-placeholder="结束日期"
      :default-time="['00:00:00', '23:59:59']"
      @change="dateChange"
      :picker-options="pickerOptions"
      size="small"
      range-separator="至"
    ></el-date-picker>
  </div>
</template>

<script>
import moment from 'moment';

const endOfToday = moment().endOf('day');
const dateFormatStr = 'yyyy-MM-DD HH:mm:ss';
const ymd = 'yyyy-MM-DD';

/**
 * 获得快捷选择项
 * @param {String} text 快捷选择展示文案
 * @param {String} startDesc 开始日期描述
 * @param {String} endDesc 结束日期描述（未传则默认今天结束点）
 * 日期描述示例：1_month_ago-距当前一个月前 / 1_month_ago_startOf-一个月前所在月的开始
 */
const shortcutItem = (startDesc, endDesc) => {
  moment.locale('uk');
  const mapSign = { ago: 'subtract', later: 'add' };
  const [snum, sunit, ssign, sdesc] = startDesc.split('_');
  const startTime = moment()[mapSign[ssign]](Number(snum), sunit).startOf(sdesc ? sunit : 'day');
  let endTime;
  if (!endDesc) {
    endTime = endOfToday;
  } else {
    const [num, unit, sign, desc] = endDesc.split('_');
    endTime = moment()[mapSign[sign]](Number(num), unit).endOf(desc ? unit : 'day');
  }
  return [startTime, endTime];
};

const mapQuickPick = {
  今天: shortcutItem('0_day_ago_startOf'),
  昨天: shortcutItem('1_day_ago_startOf', '1_day_ago_endOf'),
  最近一周: shortcutItem('1_week_ago'),
  最近一个月: shortcutItem('1_month_ago'),
  最近三个月: shortcutItem('3_month_ago'),
  本月: shortcutItem('0_month_ago_startOf', '0_month_ago_endOf'),
  上月: shortcutItem('1_month_ago_startOf', '1_month_ago_endOf'),
  本周: shortcutItem('0_week_ago_startOf', '0_week_ago_endOf'),
  上周: shortcutItem('1_week_ago_startOf', '1_week_ago_endOf'),
};

export default {
  name: 'DateTimeRangePicker',
  props: {
    maxRange: { // 最大可选日期范围，格式如'1 month'
      type: String,
      default: '',
    },
    canCrossMonth: { // 是否可以跨月
      type: Boolean,
      default: true,
    },
    shortcutArr: { // 快捷选项数组
      type: Array,
      default: () => [],
    },
    earliestDate: { // 支持查询的最早日期
      type: [Object, String],
      default: null,
    },
    latestDate: { // 支持查询的最晚日期
      type: [Object, String],
      default: null,
    },
    type: {
      type: String,
      default: 'datetimerange',
    },
    startTime: { // 双向绑定的开始时间
      type: String,
      default: '',
    },
    endTime: { // 双向绑定的结束时间
      type: String,
      default: '',
    },
  },
  computed: {
    searchTime: {
      get() {
        return [moment(this.startTime), moment(this.endTime)];
      },
      set(val) {
        // console.log('searchTime val:', val);
        const [startTime, endTime] = val.map(v => moment(v).format(this.mapFormat[this.type]));
        console.log('searchTime val:', startTime, endTime);
        this.$emit('update:startTime', startTime);
        this.$emit('update:endTime', endTime);
      },
    },
  },
  data() {
    return {
      // 注：moment的日期格式与el-date-picker日期格式不兼容，此处采用moment的
      mapFormat: {
        datetimerange: 'yyyy-MM-DD HH:mm:ss',
        daterange: 'yyyy-MM-DD',
      },

      activeIndex: -1,
      pickDate: null,

      pickerOptions: {
        onPick: (pickDateObj) => {
          const { minDate, maxDate } = pickDateObj;
          this.pickDate = (minDate && !maxDate) ? minDate : null;
        },
        disabledDate: (curDate) => {
          // 常理：不能选未来的日子
          if (moment(curDate).isAfter(moment().endOf('day'))) return true;
          // 最早限制
          if (this.earliestDate && moment(curDate).isBefore(moment(this.earliestDate))) return true;
          // 最晚限制
          if (this.latestDate && moment(curDate).isAfter(moment(this.latestDate))) return true;
          // 以下依赖于第一个日期的选择
          if (this.pickDate) {
            const mPickDate = moment(this.pickDate);
            // 不能跨月的情况
            if (!this.canCrossMonth && moment(curDate).month() !== mPickDate.month()) return true;
            // 时间范围限制的情况
            if (this.maxRange) {
              const [num, unit] = this.maxRange.split(' ');
              return (
                moment(curDate).isBefore(moment(mPickDate).subtract(Number(num), unit).add(1, 'day'))
                || moment(curDate).isAfter(moment(mPickDate).add(Number(num), unit).subtract(1, 'day'))
              );
            }
          }
          return false;
        },
      },
    };
  },
  mounted() {
    if (this.shortcutArr.length > 0) {
      this.handleQuickTimePickerChange(0); // 默认选第一个快捷项
    }
  },
  methods: {
    // [event @change] 日期更改处理
    dateChange(e) {
      this.activeIndex = -1;
      if (e) {
        const [startTime, endTime] = e.map(v => moment(v).format(this.mapFormat[this.type]));
        this.shortcutArr.forEach((e, i) => {
          const [quickStartTime, quickEndTime] = (mapQuickPick[e] || []).map(v => v.format(this.mapFormat[this.type]));
          if (startTime === quickStartTime && endTime === quickEndTime) {
            this.activeIndex = i; // 快捷项智能根据选择进行选中
          }
        });
        this.$emit('dateRangeChange', { startTime, endTime });
      } else { // 清空选择
        this.$emit('update:startTime', '');
        this.$emit('update:endTime', '');
      }
    },
    handleQuickTimePickerChange(index) {
      this.activeIndex = index;
      const [startTime, endTime] = (mapQuickPick[this.shortcutArr[index]] || []).map(v => v.format(this.mapFormat[this.type]));
      this.searchTime = [startTime, endTime];
      this.$emit('dateRangeChange', { startTime, endTime });
      this.$emit('quickSelect');
    },
  },
};
</script>

<style rel="stylesheet/scss" lang="scss" scoped>
.quick-time-picker {
  display: flex;

  .item {
    flex: 1;
    width: 100px;
    min-width: 100px;
    height: 32px;
    margin-right: 14px;
    font-size: 14px;
    line-height: 32px;
    color: #5e5e63;
    text-align: center;
    cursor: pointer;
    border: 1px solid #dde0e7;

    &.active {
      color: #008cff;
      border: 1px solid #008cff;
    }
  }
}

/deep/ .el-range-separator {
  width: 1.5em;
}
</style>
